import { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes, useId } from 'react';
import { FiAlertCircle } from 'react-icons/fi';

import CustomSelect, { CustomSelectProps } from 'components/custom-select';
import { UIElementProps } from 'types/prop-types';

/** Props comuns aos campos com rótulo (spec identidade-visual-componentes-base, D7). */
interface FieldProps {
  /** Texto do `<label>`. Obrigatório: todo campo tem nome acessível visível. */
  label: string;
  /** `id` do controle. Sem ele, um é gerado com `useId()`. */
  id?: string;
  /** Texto de ajuda abaixo do campo, ligado por `aria-describedby`. */
  hint?: string;
  /**
   * Mensagem de erro. Marca `aria-invalid` e é ligada por `aria-describedby`.
   *
   * A mensagem só é ouvida quando o campo recebe foco. No envio do formulário, a
   * tela deve mover o foco para o primeiro campo inválido (ou para um resumo de
   * erros com `role="alert"`).
   */
  error?: string;
  /** Classes do bloco (rótulo + controle + mensagens), para layout. */
  className?: string;
}

function useFieldIds(id: string | undefined, hint: string | undefined, error: string | undefined) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const hintId = hint ? `${controlId}-dica` : undefined;
  const errorId = error ? `${controlId}-erro` : undefined;
  // O erro vem antes da dica: é o que o leitor de tela precisa ouvir primeiro.
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return { controlId, hintId, errorId, describedBy };
}

interface FieldShellProps {
  label: string;
  controlId: string;
  hint?: string;
  hintId?: string;
  error?: string;
  errorId?: string;
  className?: string;
  children: ReactNode;
}

/** Rótulo, controle e mensagens: a parte comum a Input, TextArea e SelectField. */
function FieldShell({ label, controlId, hint, hintId, error, errorId, className = '', children }: FieldShellProps) {
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label htmlFor={controlId} className="font-text text-[11px] font-semibold text-content">
        {label}
      </label>
      {children}
      {hint && (
        <p id={hintId} className="font-text text-xs text-content-soft">
          {hint}
        </p>
      )}
      {/* Foco e erro usam o mesmo rosa: a mensagem em texto é o que sinaliza o erro (1.4.1). */}
      {error && (
        <p id={errorId} className="flex items-center gap-1 font-text text-xs text-content">
          <FiAlertCircle aria-hidden="true" className="shrink-0 text-brand-pink" />
          {error}
        </p>
      )}
    </div>
  );
}

const CONTROL_BASE =
  'w-full rounded-jj-field bg-brand-white font-text text-[13px] text-content placeholder:text-content-soft ' +
  'disabled:cursor-not-allowed disabled:bg-surface-100 disabled:text-content-soft';
// A borda rosa de 2px é o indicador de foco (risco de contraste aceito no D1),
// por isso o outline sai. O padding perde 1px para o texto não pular.
const CONTROL_DEFAULT =
  'border border-surface-200 px-3.5 py-[11px] ' +
  'focus:border-2 focus:border-brand-pink focus:px-[13px] focus:py-[10px] focus:outline-none';
// Com erro, a borda rosa já está lá sem foco; o foco precisa de outra mudança
// visual, então ganha o outline azul-escuro (A11Y-001).
const CONTROL_ERROR =
  'border-2 border-brand-pink px-[13px] py-[10px] ' +
  'focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-brand-blue-dark';

function controlClass(error: string | undefined) {
  return `${CONTROL_BASE} ${error ? CONTROL_ERROR : CONTROL_DEFAULT}`;
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'> & FieldProps;

export function Input({ label, id, hint, error, className, ...inputProps }: InputProps) {
  const { controlId, hintId, errorId, describedBy } = useFieldIds(id, hint, error);

  return (
    <FieldShell {...{ label, controlId, hint, hintId, error, errorId, className }}>
      <input
        {...inputProps}
        id={controlId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={controlClass(error)}
      />
    </FieldShell>
  );
}

type TextAreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'className'> & FieldProps;

export function TextArea({ label, id, hint, error, className, rows = 3, ...textAreaProps }: TextAreaProps) {
  const { controlId, hintId, errorId, describedBy } = useFieldIds(id, hint, error);

  return (
    <FieldShell {...{ label, controlId, hint, hintId, error, errorId, className }}>
      <textarea
        {...textAreaProps}
        id={controlId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={controlClass(error)}
      />
    </FieldShell>
  );
}

type SelectFieldProps = Omit<
  CustomSelectProps,
  'variant' | 'inputId' | 'ariaLabel' | 'ariaInvalid' | 'ariaErrorMessage' | 'ariaDescribedBy' | 'className'
> &
  FieldProps;

/** `CustomSelect` na variante `light`, com o mesmo rótulo e as mesmas mensagens dos outros campos. */
export function SelectField({ label, id, hint, error, className, ...selectProps }: SelectFieldProps) {
  const { controlId, hintId, errorId, describedBy } = useFieldIds(id, hint, error);

  return (
    <FieldShell {...{ label, controlId, hint, hintId, error, errorId, className }}>
      <CustomSelect
        {...selectProps}
        variant="light"
        inputId={controlId}
        ariaLabel={label}
        ariaInvalid={error ? true : undefined}
        ariaErrorMessage={errorId}
        ariaDescribedBy={describedBy}
      />
    </FieldShell>
  );
}

export function Checkbox(props: UIElementProps) {
  return <input type={'checkbox'} {...props} className={`${props.className}`} />;
}
